import { DownArrowIcon, MagicIcon, MenuIcon, StarIcon, TrashIcon } from "@xola/icons";
import { Button, FormGroup, Input, Label, Modal, Popover, Spinner } from "@xola/ui-kit";
import { useMount, useUnmount } from "ahooks";
import clsx from "clsx";
import { lowerCase, uniqueId } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { useMutation, useQueryClient } from "react-query";
import { PreferenceContext, PreferenceKey } from "types";
import shallow from "zustand/shallow";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { CustomizeDashboardModal } from "../CustomizeDashboardModal";
import { api } from "@/api/api";
import { useSeller } from "@/hooks/useAccount";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useOptimisticUpdate } from "@/hooks/useOptimisticUpdate";
import { useVisibility } from "@/hooks/useVisibility";
import { findByKey } from "@/models/Preference";
import { useDashboardFilterStore, useModalStore } from "@/stores/dashboardFilterStore";
import {
    DashboardFilter,
    DashboardMode,
    DashboardPreference,
    DashboardScope,
    ViewConfiguration,
} from "@/types/Dashboard";
import { reorder } from "@/utils/reorder";

type SavedViewsItemCallback = (viewConfiguration: ViewConfiguration) => void;

interface SavedViewsItemProps {
    viewConfiguration: ViewConfiguration;
    onClick: SavedViewsItemCallback;
    onDelete: SavedViewsItemCallback;
    isActive: boolean;
    onToggleFavourite: SavedViewsItemCallback;
    handleUpdateClick: () => void;
    index: number;
}

interface V1DashboardFilter extends DashboardFilter {
    experiences?: ID[] | null;
    guideFilter?: { all: boolean; ids: ID[]; noGuides: boolean };
    resourceFilter?: { all: boolean; ids: ID[]; noResources: boolean };
}

/**
 * Transforms V1 Filter to V2 format
 */
const transformFilter = (filter: V1DashboardFilter): DashboardFilter => {
    const transformedFilter = { ...filter };

    // In V1 we save the mode in Capitalize Format, in v2 we save in lowercase
    if (transformedFilter.mode) {
        transformedFilter.mode = lowerCase(transformedFilter.mode) as DashboardMode;
    }

    if (transformedFilter.experiences && !transformedFilter.products && transformedFilter.experiences.length > 0) {
        transformedFilter.products = { all: false, selected: transformedFilter.experiences ?? [] };
    }

    if (transformedFilter.guideFilter && !transformedFilter.guide) {
        const { guideFilter } = transformedFilter;
        transformedFilter.guide = {
            all: guideFilter.all,
            selected: guideFilter.ids ?? [],
            showUnassigned: guideFilter.noGuides ?? true,
        };
    }

    if (transformedFilter.resourceFilter && !transformedFilter.resource) {
        const { resourceFilter } = transformedFilter;
        transformedFilter.resource = {
            all: resourceFilter.all,
            selected: resourceFilter.ids ?? [],
            showUnassigned: resourceFilter.noResources ?? true,
        };
    }

    if ((transformedFilter.scope as string) === "waitlisted") {
        transformedFilter.scope = DashboardScope.WAITLIST;
    }

    return transformedFilter;
};

const SavedViewsItem = ({
    viewConfiguration,
    onClick,
    onDelete,
    isActive,
    onToggleFavourite,
    handleUpdateClick,
    index,
}: SavedViewsItemProps) => {
    const [viewId] = useState(uniqueId("custom-view-menu"));
    const handleClick = (callback: SavedViewsItemCallback) => (e: any) => {
        e.stopPropagation();
        callback(viewConfiguration);
    };

    return (
        <Draggable draggableId={viewId} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={clsx(
                        "ui-custom-view-menu-item flex h-14 w-[300px] items-center space-x-3 border-b border-gray-light p-4 hover:bg-gray-lighter ",
                        isActive && "font-bold",
                        snapshot.isDragging && "border-b-0",
                        { shadow: snapshot.isDragging },
                    )}
                    style={{ ...provided.draggableProps.style, left: "auto", top: "auto" }}
                    id={viewId}
                    role="button"
                    onClick={() => onClick(viewConfiguration)}
                >
                    <div className="shrink-0 rounded">
                        <MenuIcon size="small" />
                    </div>

                    <div className="flex-1 cursor-pointer truncate">
                        <NoTranslate className="overflow-hidden truncate text-ellipsis whitespace-nowrap" as="p">
                            {viewConfiguration.name}
                        </NoTranslate>
                    </div>

                    <div className="ml-2 flex shrink-0 items-center space-x-2">
                        {isActive && (
                            <Button type="button" size="small" onClick={handleClick(handleUpdateClick)}>
                                Save
                            </Button>
                        )}
                        <SecondaryButton
                            type="button"
                            size="small"
                            className={clsx(
                                "ui-favourite border-0 text-black hover:bg-yellow-light",
                                viewConfiguration.favourite && "bg-yellow",
                            )}
                            onClick={handleClick(onToggleFavourite)}
                        >
                            <StarIcon size="small" />
                        </SecondaryButton>
                        <InlineConfirmDeletePopover
                            placement="bottom"
                            confirmButtonText="Confirm"
                            onConfirm={handleClick(onDelete)}
                        >
                            <SecondaryButton
                                type="button"
                                size="small"
                                data-testid={`custom-view-item-delete-${viewConfiguration.id}`}
                            >
                                <TrashIcon size="small" className="p-0" />
                            </SecondaryButton>
                        </InlineConfirmDeletePopover>
                    </div>
                </div>
            )}
        </Draggable>
    );
};

export const SavedViewsButton = () => {
    const seller = useSeller();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCustomizeDashboardModal, setShowCustomizeDashboardModal] = useState(false);
    const [popoverVisible, { toggle, hide }] = useVisibility(false);

    const { data: preferences = [], isLoading } = useInfiniteCollection<DashboardPreference>("preferences");
    const [filters, setFilters, resetFilters] = useDashboardFilterStore(
        (state) => [state.filters, state.setFilters, state.resetFilters],
        shallow,
    );
    const [displayOptions, setDisplayOptions, resetDisplayOptions] = useDashboardFilterStore(
        (state) => [state.displayOptions, state.setDisplayOptions, state.resetDisplayOptions],
        shallow,
    );
    const [viewId, setViewId] = useDashboardFilterStore((state) => [state.viewId, state.setViewId], shallow);
    const queryClient = useQueryClient();
    const ref = useRef<HTMLDivElement>(null);

    // Custom hook used for persisting resources on the server.
    // Invalidates the cache after successful calls.
    const { isLoading: isSaving, mutate: savePreference } = useMutation(
        "preferences",
        async (preference: DashboardPreference) => {
            const promise = preference?.id
                ? api.patch(`/preferences/${preference.id}`, preference)
                : api.post(`/preferences`, preference);
            await promise;

            queryClient.invalidateQueries(["preferences", "collection"]);
            queryClient.invalidateQueries(["preferences", preference?.id]);
            setShowCreateModal(false);
        },
    );

    // Find dashboard preference in preferences collection.
    const currentPreference = findByKey(preferences, PreferenceKey.XOLA_SELLER_DASHBOARD) as DashboardPreference | null;

    // Custom hook, similar to `useState`, but with some differences. Best used for optimistic UI updates.
    // Allows us to modify the specified resource in the background while having snappy UI updates.
    // Uses `immer` for easier data modifications.
    const [preference, setPreference] = useOptimisticUpdate<DashboardPreference>(
        currentPreference,
        isSaving,
        (preference: DashboardPreference) => {
            savePreference(preference);
        },
    );

    // Get the list of view configurations from the dashboard preference.
    const viewConfigurations = preference?.value?.configurations ?? [];

    // Get the currently selected view configuration.
    // The one that is set in the dashboard filter store.
    const selectedViewConfiguration = viewConfigurations.find((viewConfiguration) => viewConfiguration.id === viewId);

    const favouriteViewConfiguration = viewConfigurations.find((viewConfiguration) => viewConfiguration.favourite);

    // Always select the favourite view configuration if not already selected.
    // If not already selected probably means the app just loaded.
    useEffect(() => {
        if (favouriteViewConfiguration && !viewId) {
            setViewId(favouriteViewConfiguration.id);
            setFilters(transformFilter(favouriteViewConfiguration.filter));
            if (favouriteViewConfiguration.displayOptions) {
                setDisplayOptions(favouriteViewConfiguration.displayOptions);
            }
        }
    }, [favouriteViewConfiguration, viewId, setFilters, setViewId, setDisplayOptions]);

    const handleItemClick = (viewConfiguration: ViewConfiguration) => {
        setViewId(viewConfiguration.id);
        setFilters(transformFilter(viewConfiguration.filter));
        if (viewConfiguration.displayOptions) {
            setDisplayOptions(viewConfiguration.displayOptions);
        }
    };

    const handleDefaultItemClick = () => {
        setViewId("default");
        resetFilters();
        resetDisplayOptions();
    };

    const handleCreateViewConfiguration = (name: string) => {
        const viewConfiguration = {
            id: Date.now(),
            name,
            filter: filters,
            favourite: false,
            displayOptions: displayOptions,
        };

        if (preference?.id) {
            // Preference exists, just update it.
            setPreference((preference) => {
                preference.value.configurations.push(viewConfiguration);
            });

            return;
        }

        // Create new preference.
        savePreference({
            seller: { id: seller.id },
            context: PreferenceContext.USER,
            key: PreferenceKey.XOLA_SELLER_DASHBOARD,
            value: { configurations: [viewConfiguration] },
        });
    };

    const handleUpdateClick = () => {
        const viewIndex = viewConfigurations.findIndex((viewConfiguration) => {
            return viewConfiguration.id === selectedViewConfiguration?.id;
        });

        setPreference((preference) => {
            preference.value.configurations[viewIndex] = {
                ...selectedViewConfiguration!,
                filter: filters,
                displayOptions: displayOptions,
            };
        });
    };

    const handleItemDelete = (viewConfiguration: ViewConfiguration) => {
        // Remove the view configuration from the array.
        const configurations = preference.value.configurations.filter((configuration) => {
            return configuration.id !== viewConfiguration.id;
        });

        setPreference((preference) => {
            preference.value.configurations = configurations;
        });
        handleDefaultItemClick();
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const reordered = reorder(viewConfigurations, result.source.index, result.destination.index);
        setPreference((preference) => {
            preference.value.configurations = reordered;
        });
    };

    const handleItemToggleFavourite = (viewConfiguration: ViewConfiguration) => {
        // Allows us to toggle the favourite.
        const favouriteId = viewConfiguration.favourite ? null : viewConfiguration.id;

        setPreference((preference: DashboardPreference) => {
            for (const configuration of preference.value.configurations) {
                configuration.favourite = configuration.id === favouriteId;
            }
        });
    };

    return (
        <div className="ui-custom-view-menu inline-block">
            {showCreateModal && (
                <CreateViewModal
                    isOpen={showCreateModal}
                    isLoading={isSaving}
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateViewConfiguration}
                />
            )}
            <CustomizeDashboardModal
                isOpen={showCustomizeDashboardModal}
                onClose={() => setShowCustomizeDashboardModal(false)}
            />

            <Popover
                visible={popoverVisible}
                maxWidth={300}
                offset={[0, 18]}
                placement="bottom"
                onShown={(instance: any) => {
                    ref.current?.addEventListener("click", () => {
                        instance.hide();
                    });
                }}
                onClickOutside={hide}
            >
                <div
                    className={clsx("flex max-w-30 cursor-pointer whitespace-nowrap font-bold", {
                        "no-translate": !!selectedViewConfiguration?.name,
                    })}
                    onClick={toggle}
                >
                    <span className="truncate border-b">{selectedViewConfiguration?.name ?? "Default View"}</span>
                    <span>
                        <DownArrowIcon size="small" className="text-black" />
                    </span>
                </div>

                <Popover.Content>
                    {isLoading ? (
                        <Spinner className="ui-custom-view-menu-loader" />
                    ) : (
                        <div className="max-w-[300px]">
                            <div className="flex items-center  border-b border-gray-light p-3">
                                <h3>Saved Views</h3>
                                {isSaving ? (
                                    <Spinner className="ui-custom-view-menu-inline-loader p-1" size="tiny" />
                                ) : null}
                                <div className="ml-auto">
                                    <SecondaryButton
                                        data-testid="dashboard-filter-list-customize-dashboard-button"
                                        size="small"
                                        onClick={() => {
                                            setShowCustomizeDashboardModal(true);
                                            hide();
                                        }}
                                    >
                                        <MagicIcon size="small" />
                                        <span className="ml-1 text-sm">Customize Colors</span>
                                    </SecondaryButton>
                                </div>
                            </div>

                            <DragDropContext onDragEnd={async (result: DropResult) => handleDragEnd(result)}>
                                <Droppable droppableId="savedViews">
                                    {(provided: DroppableProvided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps}>
                                            {viewConfigurations.map((viewConfiguration, index) => (
                                                <SavedViewsItem
                                                    key={viewConfiguration.id}
                                                    index={index}
                                                    isActive={viewConfiguration.id === viewId}
                                                    viewConfiguration={viewConfiguration}
                                                    handleUpdateClick={handleUpdateClick}
                                                    onClick={handleItemClick}
                                                    onDelete={handleItemDelete}
                                                    onToggleFavourite={handleItemToggleFavourite}
                                                />
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                            <div
                                className={clsx(
                                    "ui-action-default-view-item",
                                    "cursor-pointer border-b border-gray-light p-4 hover:bg-gray-lighter",
                                )}
                                role="button"
                                onClick={handleDefaultItemClick}
                            >
                                <span className="ml-4 pl-2.5">Default View</span>
                            </div>

                            <div className="p-2">
                                <div ref={ref} className="ml-44">
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setShowCreateModal(true);
                                            hide();
                                        }}
                                    >
                                        Save as new
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </Popover.Content>
            </Popover>
        </div>
    );
};

interface CreateViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (name: string) => void;
    isLoading: boolean;
}

export const CreateViewModal = ({ isOpen, onClose, onSubmit, isLoading }: CreateViewModalProps) => {
    const [name, setName] = useState("");
    const [error, setError] = useState<string | null>(null);

    // Reset name every time the modal opens.
    useEffect(() => {
        setName("");
    }, [isOpen]);

    const handleSave = () => {
        if (name.trim().length <= 0) {
            setError("is required");
        } else {
            onSubmit(name);
        }
    };

    useMount(() => {
        useModalStore.getState().setIsModalOpen(true, "Save as New View");
    });

    useUnmount(() => {
        useModalStore.getState().setIsModalOpen(false, "Save as New View");
    });

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <Modal.Header>Save as New View</Modal.Header>

            <Modal.Body>
                <fieldset disabled={isLoading}>
                    <FormGroup>
                        <Label
                            htmlFor="configurationName"
                            className={clsx("whitespace-nowrap", error && "!text-danger")}
                        >
                            Name {error}{" "}
                        </Label>
                        <Input
                            id="configurationName"
                            value={name}
                            isError={error}
                            onFocus={() => setError(null)}
                            onChange={(e: any) => setName(e.target.value)}
                        />
                    </FormGroup>
                </fieldset>
            </Modal.Body>

            <Modal.Footer>
                <fieldset className="space-x-4" disabled={isLoading}>
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                    <Button onClick={handleSave}>Save</Button>
                </fieldset>
            </Modal.Footer>
        </Modal>
    );
};
